import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Target, Clock, CheckCircle, XCircle, Loader2,
  ChevronRight, RefreshCw, Play, Square, Eye, AlertTriangle,
  BarChart2, Users, Key, Network, Database, Crown, Lock,
  Activity, Zap, Info, Search, Filter, Download, FileText,
  ChevronDown, ChevronUp, Bug, Star,
  Circle, Server, Maximize2
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
});

// ─── helpers ─────────────────────────────────────────────────────────────────

const statusBadge = (status) => {
  const map = {
    running:     { color: 'bg-blue-500/20 text-blue-400 border-blue-500/40', icon: <Loader2 className="w-3 h-3 animate-spin" />, label: 'En cours' },
    completed:   { color: 'bg-green-500/20 text-green-400 border-green-500/40', icon: <CheckCircle className="w-3 h-3" />, label: 'Completed' },
    failed:      { color: 'bg-red-500/20 text-red-400 border-red-500/40', icon: <XCircle className="w-3 h-3" />, label: 'Failed' },
    stopped:     { color: 'bg-gray-500/20 text-gray-400 border-gray-500/40', icon: <Square className="w-3 h-3" />, label: 'Stopped' },
    initialized: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40', icon: <Clock className="w-3 h-3" />, label: 'Initialized' },
  };
  const cfg = map[status] || map.initialized;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

const riskBadge = (risk) => {
  const map = {
    Critical: 'bg-red-500/20 text-red-400 border-red-500/40',
    High:     'bg-orange-500/20 text-orange-400 border-orange-500/40',
    Medium:   'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
    Low:      'bg-green-500/20 text-green-400 border-green-500/40',
    Unknown:  'bg-gray-500/20 text-gray-400 border-gray-500/40',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full border text-xs font-bold ${map[risk] || map.Unknown}`}>
      {risk || '—'}
    </span>
  );
};

const severityColor = (sev) => ({
  Critical: 'text-red-400 bg-red-500/10 border-red-500/30',
  High:     'text-orange-400 bg-orange-500/10 border-orange-500/30',
  Medium:   'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  Low:      'text-blue-400 bg-blue-500/10 border-blue-500/30',
  Info:     'text-gray-400 bg-gray-500/10 border-gray-500/30',
}[sev] || 'text-gray-400 bg-gray-500/10 border-gray-500/30');

const scoreColor = (score) => {
  if (score === null || score === undefined) return 'text-gray-400';
  if (score >= 70) return 'text-red-400';
  if (score >= 40) return 'text-orange-400';
  if (score >= 20) return 'text-yellow-400';
  return 'text-green-400';
};

const ScoreBar = ({ score, category }) => {
  const colors = {
    StaleObjects:      'bg-blue-500',
    PrivilegedAccounts:'bg-red-500',
    Trusts:            'bg-purple-500',
    Anomalies:         'bg-orange-500',
  };
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${colors[category] || 'bg-brand-green'}`}
          style={{ width: `${Math.min(100, score || 0)}%` }}
        />
      </div>
      <span className={`text-xs font-bold w-8 text-right ${scoreColor(score)}`}>{score ?? '—'}</span>
    </div>
  );
};

const phaseIcon = (phase) => {
  const icons = {
    reconnaissance:      <Eye className="w-4 h-4 text-blue-400" />,
    enumeration:         <Database className="w-4 h-4 text-purple-400" />,
    credential_harvesting:<Key className="w-4 h-4 text-yellow-400" />,
    lateral_movement:    <Network className="w-4 h-4 text-orange-400" />,
    privilege_escalation:<Crown className="w-4 h-4 text-red-400" />,
    persistence:         <Lock className="w-4 h-4 text-gray-400" />,
  };
  return icons[phase] || <Activity className="w-4 h-4 text-gray-400" />;
};

// ─── Detail Panel ─────────────────────────────────────────────────────────────

const WizardDetailPanel = ({ session, onClose, onRefresh }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('steps');
  const [liveStatus, setLiveStatus] = useState(session);
  const [polling, setPolling] = useState(session.is_running);
  const [expandedStep, setExpandedStep] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/ad-wizard/sessions/${session.session_id}/status`, { headers: getAuthHeaders() });
      if (r.ok) {
        const data = await r.json();
        console.log('[AD Wizard] status response:', {
          session_id: session.session_id,
          findings_count: (data.findings || []).length,
          steps_count: (data.steps || []).length,
          from_db: data.from_db,
          keys: Object.keys(data)
        });
        setLiveStatus(data);
        if (!data.is_running) setPolling(false);
      } else {
        console.error('[AD Wizard] status HTTP error:', r.status, r.statusText);
      }
    } catch (e) {
      console.error('[AD Wizard] status fetch error:', e);
    }
  }, [session.session_id]);

  useEffect(() => {
    fetchStatus();
    if (polling) {
      const t = setInterval(fetchStatus, 3000);
      return () => clearInterval(t);
    }
  }, [fetchStatus, polling]);

  const handleStop = async () => {
    await fetch(`${API_BASE}/api/v1/ad-wizard/sessions/${session.session_id}/stop`, { method: 'POST', headers: getAuthHeaders() });
    setPolling(false);
    fetchStatus();
    onRefresh();
  };

  const steps = liveStatus.steps || [];
  const timeline = liveStatus.timeline || [];
  const summary = liveStatus.summary || {};
  const progress = liveStatus.progress || '0/0';
  const [done, total] = progress.split('/').map(Number);
  const pct = total ? Math.round((done / total) * 100) : 0;

  // Compute findings outside JSX — step-level (live) OR session-level (DB fallback)
  const stepFindings = steps.filter(s => s.findings?.length > 0).flatMap(s =>
    s.findings.map((f, fi) => ({ key: `${s.id || s.name}-${fi}`, step: s, finding: f }))
  );
  const sessionFindings = (liveStatus.findings || []).map((f, i) => ({ key: `sf-${i}`, step: null, finding: f }));
  const allFindings = stepFindings.length > 0 ? stepFindings : sessionFindings;
  const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  const sortedFindings = [...allFindings].sort((a, b) => {
    const sA = severityOrder[(a.finding?.severity || 'info').toLowerCase()] ?? 4;
    const sB = severityOrder[(b.finding?.severity || 'info').toLowerCase()] ?? 4;
    return sA - sB;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0a0a0a] border border-brand-green/30 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl shadow-brand-green/10 mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-green/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{liveStatus.target_domain || session.target_domain}</h2>
              <p className="text-sm text-gray-400">{liveStatus.dc_ip || session.dc_ip} · Session {session.session_id.slice(0, 8)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {liveStatus.is_running && (
              <button onClick={handleStop} className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 flex items-center gap-1.5 border border-red-500/30">
                <Square className="w-3.5 h-3.5" /> Stopper
              </button>
            )}
            <button
              onClick={() => { onClose(); navigate(`/ad/session/${session.session_id}`); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-green/10 text-brand-green rounded-lg text-sm hover:bg-brand-green/20 border border-brand-green/20 transition-colors"
              title="Open in dedicated page"
            >
              <Maximize2 className="w-3.5 h-3.5" /> Pleine page
            </button>
            <button onClick={fetchStatus} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 border-b border-gray-800/50">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-400">Progress — {progress} steps</span>
            <span className="text-xs font-bold text-brand-green">{pct}%</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-brand-green to-green-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center gap-4 mt-2">
            {statusBadge(liveStatus.status || session.status)}
            <span className="text-xs text-gray-500">Phase : <span className="text-gray-300">{liveStatus.current_phase || '—'}</span></span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-gray-800">
          {[
            { id: 'steps',    label: 'Steps', icon: <Activity className="w-3.5 h-3.5" /> },
            { id: 'timeline', label: 'Timeline', icon: <Clock className="w-3.5 h-3.5" /> },
            { id: 'findings', label: `Findings${allFindings.length > 0 ? ` (${allFindings.length})` : ''}`, icon: <Bug className="w-3.5 h-3.5" /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-brand-green text-brand-green' : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ── Steps tab ── */}
          {activeTab === 'steps' && (
            <div className="space-y-2">
              {steps.map((step, i) => (
                <div key={step.id || i} className={`rounded-lg border overflow-hidden transition-all ${
                  step.status === 'completed' ? 'border-green-500/30' :
                  step.status === 'failed'    ? 'border-red-500/30' :
                  step.status === 'running'   ? 'border-blue-500/30' :
                  step.status === 'skipped'   ? 'border-gray-700' : 'border-gray-800'
                }`}>
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/[0.02]"
                    onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                  >
                    <div className="flex-shrink-0">
                      {step.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-400" /> :
                       step.status === 'failed'    ? <XCircle className="w-4 h-4 text-red-400" /> :
                       step.status === 'running'   ? <Loader2 className="w-4 h-4 text-blue-400 animate-spin" /> :
                       step.status === 'skipped'   ? <AlertTriangle className="w-4 h-4 text-yellow-400" /> :
                                                     <Circle className="w-4 h-4 text-gray-600" />}
                    </div>
                    <div className="flex-shrink-0">{phaseIcon(step.phase)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white truncate">{step.name}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-gray-800 text-gray-500 rounded">{step.mitre_id}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">{step.tool} · {step.phase}</div>
                    </div>
                    {step.findings?.length > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full border border-orange-500/30">
                        {step.findings.length} finding{step.findings.length > 1 ? 's' : ''}
                      </span>
                    )}
                    {expandedStep === step.id ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                  </div>

                  {expandedStep === step.id && (
                    <div className="px-4 pb-4 border-t border-gray-800/50 space-y-3 pt-3">
                      <p className="text-sm text-gray-400">{step.description}</p>
                      {step.command && (
                        <div className="bg-black/40 rounded-lg p-3 font-mono text-xs text-green-400 border border-gray-800">
                          {step.command}
                        </div>
                      )}
                      {step.output && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Output :</div>
                          <div className="bg-black/40 rounded-lg p-3 font-mono text-xs text-gray-300 max-h-32 overflow-y-auto border border-gray-800 whitespace-pre-wrap">
                            {step.output}
                          </div>
                        </div>
                      )}
                      {(step.ai_analysis || step.ai_explanation) && (
                        <div className="bg-brand-green/5 border border-brand-green/20 rounded-lg p-3">
                          <div className="text-xs text-brand-green font-semibold mb-1">Analyse IA :</div>
                          <p className="text-xs text-gray-300">{step.ai_analysis || step.ai_explanation}</p>
                        </div>
                      )}
                      {step.findings?.length > 0 && (
                        <div className="space-y-1">
                          <div className="text-xs text-gray-500">Findings :</div>
                          {step.findings.map((f, fi) => (
                            <div key={fi} className="text-xs bg-orange-500/10 border border-orange-500/20 rounded px-2 py-1 text-orange-300">
                              {typeof f === 'string' ? f : JSON.stringify(f)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {steps.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-sm">No steps available</div>
              )}
            </div>
          )}

          {/* ── Timeline tab ── */}
          {activeTab === 'timeline' && (
            <div className="space-y-3">
              {timeline.map((event, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-2 h-2 rounded-full mt-1.5 ${
                      event.event_type === 'step_completed' ? 'bg-green-400' :
                      event.event_type === 'step_failed'   ? 'bg-red-400' :
                      event.event_type === 'finding_discovered' ? 'bg-orange-400' :
                      'bg-blue-400'
                    }`} />
                    {i < timeline.length - 1 && <div className="w-px flex-1 bg-gray-800 mt-1" />}
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="text-xs text-gray-500">{new Date(event.timestamp).toLocaleTimeString()}</div>
                    <div className="text-sm text-white font-medium">{event.step_name || event.event}</div>
                    {event.description && <p className="text-xs text-gray-400 mt-0.5">{event.description}</p>}
                    {event.ai_explanation && (
                      <p className="text-xs text-brand-green/80 mt-1 italic">{event.ai_explanation}</p>
                    )}
                  </div>
                </div>
              ))}
              {timeline.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-sm">No events</div>
              )}
            </div>
          )}

          {/* ── Findings tab ── */}
          {activeTab === 'findings' && (
            sortedFindings.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                <CheckCircle className="w-8 h-8 text-green-500/50 mx-auto mb-2" />
                No findings detected
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-gray-500 mb-3">{sortedFindings.length} finding{sortedFindings.length > 1 ? 's' : ''} detected</div>
                {sortedFindings.map(({ key, step, finding }) => {
                  const sev = (finding?.severity || 'info').toLowerCase();
                  const sevColor = {
                    critical: 'bg-red-500/10 border-red-500/30 text-red-400',
                    high:     'bg-orange-500/10 border-orange-500/30 text-orange-400',
                    medium:   'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
                    low:      'bg-blue-500/10 border-blue-500/30 text-blue-400',
                    info:     'bg-gray-500/10 border-gray-700 text-gray-400',
                  }[sev] || 'bg-gray-500/10 border-gray-700 text-gray-400';

                  const title = finding?.title || (typeof finding === 'string' ? finding : finding?.finding || JSON.stringify(finding));
                  const desc = finding?.description || finding?.evidence || '';
                  const reco = finding?.recommendation || '';

                  return (
                    <div key={key} className={`p-3 rounded-lg border ${sevColor}`}>
                      <div className="flex items-start gap-2">
                        <Bug className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-white">{title}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-bold ${sevColor}`}>
                              {sev.toUpperCase()}
                            </span>
                            {finding?.finding_type && (
                              <span className="text-xs text-gray-500">{finding.finding_type}</span>
                            )}
                            {step && (
                              <span className="flex items-center gap-1 text-xs text-gray-500">
                                {phaseIcon(step.phase)} {step.name}
                              </span>
                            )}
                          </div>
                          {desc && <p className="text-xs text-gray-400 mt-1">{desc}</p>}
                          {reco && (
                            <p className="text-xs text-green-400/80 mt-1.5">
                              <span className="font-semibold text-green-400">Remediation: </span>{reco}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Audit AD Detail Panel ────────────────────────────────────────────────────

const AuditADDetailPanel = ({ scan, onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('scores');
  const [liveData, setLiveData] = useState(scan);

  const fetchDetail = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/v1/pingcastle/scans/${scan.scan_id}`, { headers: getAuthHeaders() });
      if (r.ok) setLiveData(await r.json());
    } catch (_) {}
  }, [scan.scan_id]);

  useEffect(() => {
    if (scan.status === 'running') {
      const t = setInterval(async () => {
        await fetchDetail();
        if (liveData.status !== 'running') clearInterval(t);
      }, 3000);
      return () => clearInterval(t);
    }
    fetchDetail();
  }, [fetchDetail, scan.status]);

  const result = liveData.result || {};
  const scores = result.scores || {};
  const indicators = result.indicators || [];
  const stats = result.stats || {};
  const byCategory = indicators.reduce((acc, ind) => {
    acc[ind.category] = acc[ind.category] || [];
    acc[ind.category].push(ind);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0a0a0a] border border-purple-500/30 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl shadow-purple-500/10 mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Star className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Audit AD — {scan.domain}</h2>
              <p className="text-sm text-gray-400">{scan.dc_ip} · {new Date(scan.started_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onClose(); navigate(`/ad/pingcastle/${scan.scan_id}`); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-xs hover:bg-purple-500/20 transition-colors border border-purple-500/20"
            >
              <Maximize2 className="w-3.5 h-3.5" /> Pleine page
            </button>
            <button onClick={fetchDetail} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status / global score */}
        {liveData.status === 'running' && (
          <div className="px-6 py-3 border-b border-gray-800/50 flex items-center gap-3 text-blue-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Audit en cours…</span>
          </div>
        )}
        {liveData.status === 'completed' && result.global_score !== undefined && (
          <div className="px-6 py-3 border-b border-gray-800/50">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className={`text-4xl font-black ${scoreColor(result.global_score)}`}>{result.global_score}</div>
                <div className="text-xs text-gray-500">Score global /100</div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                {Object.entries(scores).map(([cat, score]) => (
                  <div key={cat}>
                    <div className="text-xs text-gray-500 mb-1">{cat}</div>
                    <ScoreBar score={score} category={cat} />
                  </div>
                ))}
              </div>
              <div>{riskBadge(result.risk_level)}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-gray-800">
          {[
            { id: 'scores',     label: 'Scores',      icon: <BarChart2 className="w-3.5 h-3.5" /> },
            { id: 'indicators', label: `Indicateurs (${indicators.length})`, icon: <AlertTriangle className="w-3.5 h-3.5" /> },
            { id: 'stats',      label: 'Statistiques', icon: <Activity className="w-3.5 h-3.5" /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-purple-500 text-purple-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'scores' && (
            <div className="space-y-4">
              {Object.entries(scores).length === 0 && (
                <p className="text-center text-gray-500 py-8 text-sm">{liveData.status === 'running' ? 'Calcul des scores en cours…' : 'Aucun score disponible'}</p>
              )}
              {Object.entries(scores).map(([cat, score]) => (
                <div key={cat} className="bg-brand-black-light rounded-xl p-4 border border-gray-800">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-white">{cat}</h3>
                    <span className={`text-2xl font-black ${scoreColor(score)}`}>{score}</span>
                  </div>
                  <ScoreBar score={score} category={cat} />
                  <div className="mt-3 space-y-1">
                    {(byCategory[cat] || []).map((ind, i) => (
                      <div key={i} className={`text-xs px-2 py-1 rounded border ${severityColor(ind.severity)}`}>
                        [{ind.severity}] {ind.title}
                      </div>
                    ))}
                    {!byCategory[cat] && <p className="text-xs text-green-400">✓ No issues detected</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'indicators' && (
            <div className="space-y-3">
              {indicators.length === 0 && (
                <div className="text-center py-8">
                  <CheckCircle className="w-8 h-8 text-green-500/50 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Aucun indicateur de risque</p>
                </div>
              )}
              {indicators.map((ind, i) => (
                <div key={i} className={`rounded-xl p-4 border ${severityColor(ind.severity)}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-white">{ind.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${severityColor(ind.severity)}`}>{ind.severity}</span>
                        <span className="text-xs text-gray-500">{ind.category} · {ind.id}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{ind.description}</p>
                      {ind.solution && (
                        <p className="text-xs text-green-400/80 mt-2">
                          <span className="font-semibold text-green-400">Solution : </span>{ind.solution}
                        </p>
                      )}
                    </div>
                    {ind.matched_count > 0 && (
                      <span className="text-xs font-bold text-white bg-gray-700 px-2 py-1 rounded-full">{ind.matched_count}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'Utilisateurs totaux',       value: stats.total_users,             icon: <Users className="w-5 h-5 text-blue-400" /> },
                { label: 'Ordinateurs totaux',        value: stats.total_computers,         icon: <Server className="w-5 h-5 text-purple-400" /> },
                { label: 'Domain Admins',             value: stats.domain_admins,           icon: <Crown className="w-5 h-5 text-red-400" /> },
                { label: 'Kerberoastable',            value: stats.kerberoastable,          icon: <Key className="w-5 h-5 text-orange-400" /> },
                { label: 'AS-REP Roastable',          value: stats.asreproastable,          icon: <Zap className="w-5 h-5 text-yellow-400" /> },
                { label: 'MDP sans expiration',       value: stats.never_expires,           icon: <Lock className="w-5 h-5 text-gray-400" /> },
                { label: 'Ordinateurs inactifs',      value: stats.stale_computers,         icon: <AlertTriangle className="w-5 h-5 text-yellow-400" /> },
                { label: 'Utilisateurs inactifs',     value: stats.stale_users,             icon: <AlertTriangle className="w-5 h-5 text-orange-400" /> },
                { label: 'Unconstrained delegation', value: stats.unconstrained_delegation, icon: <Network className="w-5 h-5 text-red-400" /> },
              ].map(({ label, value, icon }) => (
                <div key={label} className="bg-brand-black-light rounded-xl p-4 border border-gray-800 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">{icon}</div>
                  <div>
                    <div className={`text-2xl font-black ${value > 0 ? 'text-white' : 'text-gray-600'}`}>
                      {value ?? '—'}
                    </div>
                    <div className="text-xs text-gray-500">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ADPentestResultsTable = ({ dcConfig, workspaceId }) => {
  const navigate = useNavigate();
  const [wizardSessions, setWizardSessions] = useState([]);
  const [pingcastleScans, setPingcastleScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('wizard');
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedPCScan, setSelectedPCScan] = useState(null);
  const [pcRunning, setPcRunning] = useState(false);
  const [filter, setFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = workspaceId ? `?workspace_id=${workspaceId}` : '';
      const [wizR, pcR] = await Promise.all([
        fetch(`${API_BASE}/api/v1/ad-wizard/sessions${params}`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/api/v1/pingcastle/scans${params}`, { headers: getAuthHeaders() }),
      ]);
      if (wizR.ok) {
        const data = await wizR.json();
        setWizardSessions(data.sessions || []);
      }
      if (pcR.ok) {
        const data = await pcR.json();
        setPingcastleScans(data.scans || []);
      }
    } catch (e) {
      console.error('Failed to fetch AD pentest data', e);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 10000);
    return () => clearInterval(t);
  }, [fetchData]);

  const launchPingCastle = async () => {
    if (!dcConfig?.dc_ip || !dcConfig?.domain) {
      alert('Configurez le DC IP et le domaine avant de lancer l\'audit AD');
      return;
    }
    setPcRunning(true);
    try {
      const r = await fetch(`${API_BASE}/api/v1/pingcastle/scan`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          dc_ip: dcConfig.dc_ip,
          domain: dcConfig.domain,
          username: dcConfig.username || null,
          password: dcConfig.password || null,
          workspace_id: workspaceId || null,
        }),
      });
      if (r.ok) {
        setActiveTab('pingcastle');
        fetchData();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setPcRunning(false);
    }
  };

  // Filter sessions — workspace_id client-side (safety net if API returns all)
  const filteredSessions = wizardSessions.filter(s =>
    (!workspaceId || !s.workspace_id || s.workspace_id === workspaceId) &&
    (!filter || s.target_domain?.toLowerCase().includes(filter.toLowerCase()) ||
    s.dc_ip?.includes(filter))
  );
  const filteredPCScans = pingcastleScans.filter(s =>
    (!workspaceId || !s.workspace_id || s.workspace_id === workspaceId) &&
    (!filter || s.domain?.toLowerCase().includes(filter.toLowerCase()) ||
    s.dc_ip?.includes(filter))
  );

  return (
    <div className="bg-brand-black-light rounded-2xl border border-brand-green/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-brand-green/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-brand-green" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">AD Pentest Results</h2>
            <p className="text-xs text-gray-500">Wizard d'attaque · Audits AD · 71 techniques OCD</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Audit AD launch button */}
          <button
            onClick={launchPingCastle}
            disabled={pcRunning || !dcConfig?.dc_ip}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white text-sm font-semibold hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 transition-all shadow-lg shadow-purple-500/20"
            title={!dcConfig?.dc_ip ? 'Configurez le DC IP' : 'Lancer un audit AD'}
          >
            {pcRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
            Audit AD
          </button>
          <button onClick={fetchData} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors" title="Actualiser">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-3 border-b border-gray-800 bg-gray-900/20">
        <button
          onClick={() => setActiveTab('wizard')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'wizard' ? 'border-brand-green text-brand-green' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          <Shield className="w-4 h-4" />
          Wizard AD
          <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'wizard' ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-800 text-gray-500'}`}>
            {wizardSessions.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('pingcastle')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === 'pingcastle' ? 'border-purple-500 text-purple-400' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          <Star className="w-4 h-4" />
          Audit AD
          <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'pingcastle' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-800 text-gray-500'}`}>
            {pingcastleScans.length}
          </span>
        </button>
      </div>

      {/* Search */}
      {(
        <div className="px-6 py-3 border-b border-gray-800/50">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filtrer par domaine, IP…"
              className="w-full pl-9 pr-3 py-1.5 bg-brand-black border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:border-brand-green outline-none"
            />
          </div>
        </div>
      )}

      {/* ── Wizard sessions table ── */}
      {activeTab === 'wizard' && (
        <div className="overflow-x-auto">
          {loading && wizardSessions.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-gray-500 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Shield className="w-10 h-10 text-gray-700 mb-3" />
              <p className="text-gray-500 text-sm">Aucune session de pentest AD</p>
              <p className="text-gray-600 text-xs mt-1">Lancez une attaque via le bouton "Attack Wizard" (71 techniques OCD)</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left">
                  <th className="px-6 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Domaine</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">DC IP</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Progression</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Started</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Finished</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {filteredSessions.map(session => {
                  const [done, total] = (session.progress || '0/0').split('/').map(Number);
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  return (
                    <tr
                      key={session.session_id}
                      className="hover:bg-white/[0.02] transition-colors group"
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-brand-green/60" />
                          <span className="font-semibold text-white">{session.target_domain || '—'}</span>
                        </div>
                        <div className="text-xs text-gray-600 ml-6">{session.session_id.slice(0, 12)}…</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-300">{session.dc_ip || '—'}</td>
                      <td className="px-4 py-3">{statusBadge(session.status || 'initialized')}</td>
                      <td className="px-4 py-3 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-brand-green to-green-400 rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-12 text-right">{session.progress}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">{pct}%</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {session.started_at ? new Date(session.started_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {session.completed_at ? new Date(session.completed_at).toLocaleString() : <span className="text-gray-700">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setSelectedSession(session)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-green/10 text-brand-green rounded-lg text-xs hover:bg-brand-green/20 transition-colors border border-brand-green/20 group-hover:border-brand-green/40"
                          >
                            <Eye className="w-3.5 h-3.5" /> Details
                          </button>
                          <button
                            onClick={() => navigate(`/ad/session/${session.session_id}`)}
                            className="flex items-center gap-1 px-2 py-1.5 bg-gray-800 text-gray-400 rounded-lg text-xs hover:bg-gray-700 hover:text-white transition-colors border border-gray-700"
                            title="Open in dedicated page"
                          >
                            <Maximize2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── PingCastle scans table ── */}
      {activeTab === 'pingcastle' && (
        <div className="overflow-x-auto">
          {loading && pingcastleScans.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-gray-500 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
            </div>
          ) : filteredPCScans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Star className="w-10 h-10 text-gray-700 mb-3" />
              <p className="text-gray-500 text-sm">Aucun audit AD</p>
              <p className="text-gray-600 text-xs mt-1">Click "AD Audit" to start an audit</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left">
                  <th className="px-6 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Domaine</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">DC IP</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Score Global</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Risque</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Indicateurs</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {filteredPCScans.map(scan => (
                  <tr key={scan.scan_id} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-purple-400/60" />
                        <span className="font-semibold text-white">{scan.domain || '—'}</span>
                      </div>
                      <div className="text-xs text-gray-600 ml-6">{scan.scan_id.slice(0, 12)}…</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-300">{scan.dc_ip || '—'}</td>
                    <td className="px-4 py-3">{statusBadge(scan.status)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-2xl font-black ${scoreColor(scan.global_score)}`}>
                        {scan.global_score ?? <span className="text-gray-600 text-sm">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">{scan.risk_level ? riskBadge(scan.risk_level) : <span className="text-gray-600">—</span>}</td>
                    <td className="px-4 py-3">
                      {scan.total_indicators !== null ? (
                        <span className={`font-semibold ${scan.total_indicators > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                          {scan.total_indicators}
                        </span>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {new Date(scan.started_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedPCScan(scan)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-xs hover:bg-purple-500/20 transition-colors border border-purple-500/20 group-hover:border-purple-500/40"
                        >
                          <Eye className="w-3.5 h-3.5" /> Rapport
                        </button>
                        <button
                          onClick={() => navigate(`/ad/pingcastle/${scan.scan_id}`)}
                          className="p-1.5 text-purple-400/60 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                          title="Voir en pleine page"
                        >
                          <Maximize2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Detail modals */}
      {selectedSession && (
        <WizardDetailPanel
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onRefresh={fetchData}
        />
      )}
      {selectedPCScan && (
        <AuditADDetailPanel
          scan={selectedPCScan}
          onClose={() => setSelectedPCScan(null)}
        />
      )}
    </div>
  );
};

export default ADPentestResultsTable;
